/**
 * Pure H OS · Sprint 12 · Finance mocks — types + live views.
 * Payload now lives in MongoDB `pureh_*` collections.
 */
import { liveArray, liveSingleton } from "../services/store";

// ── Revenue ──────────────────────────────────────────────────────
export type RevenueStream = {
  key: string;
  label: string;
  category: "web" | "wholesale" | "pop-up" | "affiliate" | "b2b";
  monthEGP: number;
  ytdEGP: number;
  delta: number;
  trend: "up" | "down" | "flat";
};
export const REVENUE_STREAMS: RevenueStream[] = liveArray<RevenueStream>("revenue_streams");

export type ProductRevenue = { sku: string; name: string; unitsSold: number; revenueEGP: number; grossMarginPct: number };
export const PRODUCT_REVENUE: ProductRevenue[] = liveArray<ProductRevenue>("product_revenue");

// ── Expenses ─────────────────────────────────────────────────────
export type ExpenseCategory = "cogs" | "marketing" | "salaries" | "logistics" | "rent" | "software" | "other";
export type Expense = {
  id: string;
  vendor: string;
  category: ExpenseCategory;
  description: string;
  amountEGP: number;
  paidRelative: string;
  status: "paid" | "scheduled" | "overdue";
};
export const EXPENSES: Expense[] = liveArray<Expense>("expenses");

export type ExpenseCategoryTotal = { key: ExpenseCategory; label: string; monthEGP: number; delta: number; trend: "up" | "down" | "flat" };
export const EXPENSE_TOTALS: ExpenseCategoryTotal[] = liveArray<ExpenseCategoryTotal>("expense_totals");

// ── Cash flow ────────────────────────────────────────────────────
export type CashPoint = { month: string; inflow: number; outflow: number; balance: number };
export const CASH_FLOW: CashPoint[] = liveArray<CashPoint>("cash_flow");

/**
 * Index in `CASH_FLOW` where the forecast section begins. Rendered as a
 * dotted line beyond this index. Backed by the singleton
 * `pureh_cash_forecast_start_index` collection (`{ value: number }`).
 *
 * The value is looked up lazily at property access via a `Proxy`, so the
 * consumer can keep treating it as a `number` — arithmetic and comparison
 * work through Symbol.toPrimitive.
 */
const _CFSI = liveSingleton<{ value: number }>("cash_forecast_start_index", { value: 0 });
// Proxy target must be an object; we cast the export to `number` so
// consumers can use it in comparisons/arithmetic. `Symbol.toPrimitive`
// resolves the current MongoDB value at every use.
const _CFSI_HANDLE: object = new Proxy({}, {
  get(_t, prop) {
    const v = _CFSI.value ?? 0;
    if (prop === Symbol.toPrimitive) return () => v;
    if (prop === "valueOf") return () => v;
    if (prop === "toString") return () => String(v);
    const num = v as unknown as { [k: string | symbol]: unknown };
    return num[prop as string | symbol];
  },
});
export const CASH_FORECAST_START_INDEX: number = _CFSI_HANDLE as unknown as number;

// ── Budget ───────────────────────────────────────────────────────
export type BudgetLine = {
  key: string;
  category: string;
  plannedEGP: number;
  actualEGP: number;
  ownerDepartment: string;
};
export const BUDGET: BudgetLine[] = liveArray<BudgetLine>("budget");

// ── Invoices ────────────────────────────────────────────────────
export type InvoiceStatus = "draft" | "sent" | "partial" | "paid" | "overdue";
export type Invoice = {
  id: string;
  number: string;
  customer: string;
  city: string;
  amountEGP: number;
  paidEGP: number;
  issued: string;
  dueRelative: string;
  status: InvoiceStatus;
};
export const INVOICES: Invoice[] = liveArray<Invoice>("invoices");

// ── Vendor Payments ─────────────────────────────────────────────
export type VendorPaymentStatus = "queued" | "approved" | "sent" | "cleared" | "on-hold";
export type VendorPayment = {
  id: string;
  vendor: string;
  reference: string;
  amountEGP: number;
  currency: string;
  dueRelative: string;
  status: VendorPaymentStatus;
  method: "wire" | "card" | "cash" | "internal";
  approvedBy?: string;
};
export const VENDOR_PAYMENTS: VendorPayment[] = liveArray<VendorPayment>("vendor_payments");

// ── Financial Risks ─────────────────────────────────────────────
export type FinRiskCategory = "fx" | "receivables" | "runway" | "concentration" | "margin" | "compliance";
export type FinRisk = {
  id: string;
  title: string;
  category: FinRiskCategory;
  severity: "high" | "medium" | "low";
  exposureEGP: number;
  note: string;
  aiConfidence: number;
  status: "open" | "monitoring" | "mitigated";
};
export const FIN_RISKS: FinRisk[] = liveArray<FinRisk>("fin_risks");

// ── Overview KPIs (singleton) ───────────────────────────────────
type Kpi = { key: string; label: string; value: string; delta: number; trend: string; hint?: string };
export type FinanceOverview = { kpis: Kpi[] };
export const FINANCE_OVERVIEW: FinanceOverview = liveSingleton<FinanceOverview>(
  "finance_overview",
  { kpis: [] },
);
